import React, { useEffect, useMemo, useState } from 'react';
import { useGamesState } from '../../../../api/games/useGames';
import { toErrorMessage } from '../../../../api/error/error';
import { Grid } from '@material-ui/core';
import { DataContainer } from '../../../../components/layout/DataContainer';
import { Pagination } from '@material-ui/lab';
import { ResultListEntry } from './ResultListEntry';
import { Game } from '../../../../api/games/model';

type ResultsListProps = {
  readonlyFinishedGames?: Game[];
};

export const ResultsList = ({ readonlyFinishedGames }: ResultsListProps) => {
  const { finishedGames: loadedFinishedGames, isSuccess, isLoading, isError, error } = useGamesState();
  const [currentPage, setCurrentPage] = useState(1);
  const [finishedGames, setFinishedGames] = useState<Game[]>([]);
  const { totalPages, paginatedItems } = usePagination(finishedGames ?? [], 10, currentPage);

  useEffect(() => {
    if (readonlyFinishedGames) {
      setFinishedGames(readonlyFinishedGames);
      return;
    }
    if (loadedFinishedGames) {
      setFinishedGames(loadedFinishedGames);
    }
  }, [readonlyFinishedGames, loadedFinishedGames]);

  const defaultEmptyMessage = 'Keine Ergebnisse vorhanden.';
  const determineEmptyMessage = () => {
    if (isLoading) return 'Ergebnisse werden geladen';
    if (isError) return `Fehler beim Laden.\n${toErrorMessage(error)}`;
    return defaultEmptyMessage;
  };

  const handlePaginationChange = (_, pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <DataContainer
      isLoading={readonlyFinishedGames ? false : isLoading}
      isSuccess={readonlyFinishedGames ? true : isSuccess}
      isEmpty={!finishedGames || finishedGames.length === 0}
      emptyMessage={readonlyFinishedGames ? defaultEmptyMessage : determineEmptyMessage()}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div
            style={{
              overflowY: 'hidden',
              display: 'flex',
              gap: '12px',
              paddingBottom: '8px',
              paddingRight: '4px',
              flexDirection: 'column',
              minHeight: totalPages > 1 ? '810px' : undefined,
            }}
          >
            {paginatedItems.map((finishedGame) => {
              return <ResultListEntry key={finishedGame.id} finishedGame={finishedGame} />;
            })}
          </div>
        </Grid>
        <Grid item xs={12} container justifyContent="center">
          {finishedGames && finishedGames.length > 0 && (
            <Pagination
              count={totalPages}
              variant="outlined"
              shape="rounded"
              siblingCount={0}
              onChange={handlePaginationChange}
            />
          )}
        </Grid>
      </Grid>
    </DataContainer>
  );
};

type UsePaginationReturnType<T> = {
  paginatedItems: T[];
  totalPages: number;
};

function usePagination<T>(items: T[], pageSize: number, currentPage: number): UsePaginationReturnType<T> {
  const totalPages = Math.ceil(items.length / pageSize);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return items.slice(start, end);
  }, [items, pageSize, currentPage]);

  return { paginatedItems, totalPages };
}

export default usePagination;
